'use client';

import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { TextPassage } from '@petsmart-ui/sparky';
import { useGoogleRecaptcha } from './useGoogleRecaptcha';
import { isTestRun } from './helpers';
import { logSentryEvent } from '@libs/common-next/utils';

export function GoogleRecaptchaCheckbox({
    className,
    id,
    page = '',
    parent,
}: {
    className?: string;
    id: string;
    page?: string;
    parent?: string;
}): ReactElement | null {
    const { recaptchaErrorCheckbox, renderRecaptchaCheckbox, useRecaptchaCheckbox } = useGoogleRecaptcha();

    const shouldRenderCheckbox = !isTestRun() && useRecaptchaCheckbox === id;
    const isRecaptchaError = shouldRenderCheckbox && recaptchaErrorCheckbox;

    /**
     * Update recaptcha position during window resize (technical debt)
     */
    useEffect(() => {
        const resizeHandler = () => {
            window.dispatchEvent(new WheelEvent('scroll'));
        };
        window.addEventListener('resize', resizeHandler);
        return () => window.removeEventListener('resize', resizeHandler);
    }, []);

    useEffect(() => {
        if (shouldRenderCheckbox) {
            void renderRecaptchaCheckbox(id);
        }
    }, [id, useRecaptchaCheckbox]);

    if (shouldRenderCheckbox) {
        logSentryEvent('Recaptcha checkbox rendered', {
            sentryContext: {
                action: 'RECAPTCHA_CHECKBOX_RENDER',
                page,
            },
            tags: {
                component: parent,
                page,
            },
        });
    }

    return shouldRenderCheckbox ? (
        <div className={className} data-testid="recaptcha-checkbox">
            {isRecaptchaError && (
                <TextPassage className="sparky-u-color-utility-error">{recaptchaErrorCheckbox}</TextPassage>
            )}
            <div id={id} />
        </div>
    ) : null;
}
